import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { useLocale } from '@/renderer/utils/locale';
import { useData } from '@/renderer/utils/useData';

import type { IndexData, WallsPost } from './dataFetcher';

export default WallsBlogIndex;

function WallsBlogIndex() {
  const { posts } = useData<IndexData>();

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="Walls' Blog" className="py-2 md:hidden" />
      <div className="overflow-auto">
        {/* ── Hero ── */}
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl pt-12 pb-8 sm:pt-20">
            <h1 className="font-bold text-3xl text-zinc-900 tracking-tight sm:text-4xl dark:text-zinc-100">
              Walls' Blog
            </h1>
            <p className="mt-4 text-lg text-zinc-600 leading-7 dark:text-zinc-400">
              Walls is a long-time pro bettor and RAS team member. He shares his thoughts on sports
              betting, life, and more.
            </p>
          </div>
        </div>

        {/* ── Post list ── */}
        <div className="px-4 pb-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            {posts.length === 0 ? (
              <p className="py-12 text-center text-zinc-500 dark:text-zinc-400">
                No posts yet — check back soon.
              </p>
            ) : (
              <ul className="-mx-4 space-y-1">
                {posts.map((post) => (
                  <PostListItem key={post.id} post={post} />
                ))}
              </ul>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}

function PostListItem({ post }: { post: WallsPost }) {
  const { formatDate } = useLocale();

  return (
    <li>
      <a
        href={`/walls/${post.slug}`}
        className="group flex flex-col gap-4 rounded-xl p-4 transition hover:bg-zinc-950/[0.03] sm:flex-row sm:p-5 dark:hover:bg-white/5"
      >
        {post.coverImage && (
          <img
            src={post.coverImage}
            alt=""
            className="aspect-[16/9] w-full shrink-0 rounded-lg object-cover sm:w-44"
            loading="lazy"
          />
        )}
        <div className="min-w-0">
          {post.publishedAt && (
            <time className="font-medium text-emerald-600 text-xs dark:text-emerald-400">
              {formatDate(post.publishedAt, { year: 'numeric', month: 'long', day: 'numeric' })}
            </time>
          )}
          <h2 className="mt-1 font-semibold text-lg text-zinc-900 group-hover:text-emerald-600 dark:text-zinc-100 dark:group-hover:text-emerald-400">
            {post.title}
          </h2>
          {post.excerpt && (
            <p className="mt-1.5 text-sm text-zinc-600 leading-6 dark:text-zinc-400">
              {post.excerpt}
            </p>
          )}
        </div>
      </a>
    </li>
  );
}
